import {useEffect, useRef, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../hooks/StoreHooks";
import {clearSuccessMessage, removeErrorByIndex} from "../reducers/requestStateSlice";

import {Alert, Box, Slide, Stack, Typography} from "@mui/material";

import {ErrorResponse} from "../../api/Requests";
import i18n from "../../i18n";
import {ErrorCode} from "../../api/errorCode";
import {CompleteInstanceValidationResult} from "../../types/CompleteInstanceValidationResult";

export function RequestNotifier() {
  const dispatch = useAppDispatch();
  const { errorResponses, successMessage } = useAppSelector((state) => state.requestState);
  
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (successMessage) {
      
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      setMessage(successMessage);
      setOpen(true);
      dispatch(clearSuccessMessage());

      timerRef.current = setTimeout(() => {
        setOpen(false);
        timerRef.current = null;
        // Slide animation before reset
        setTimeout(() => setMessage(null), 500);
      }, 4000);
    }
  }, [successMessage, dispatch]);

  const handleCloseSuccessMessage = () => {
    setOpen(false);
    if (timerRef.current){
      clearTimeout(timerRef.current);
    } 
    setTimeout(() => setMessage(null), 500);
  };

  const formatError = (res: ErrorResponse) => {
    const info = i18n.exists(`errorCode.${res.errorCode}`) ? i18n.t(`errorCode.${res.errorCode}`) : i18n.t("errorCode.Generic");
    const errorCode = res.errorCode;
    const errorMessage = res.message;
    const traceId = res.traceId ?? "N/A";

    function defaultErrorMessage() {
      return `${info}\nMessage: ${errorMessage}\nError code: ${errorCode}\nTrace-Id: ${traceId}`;
    }

    switch (res.errorCode) {      
      case ErrorCode.IncentivationImportFailed:
      {
          const data = res.data as CompleteInstanceValidationResult;
          if (data !== undefined && data?.validationErrors !== undefined) {
              return defaultErrorMessage() + `\nIs valid: ${data.isValid}\nValidation errors:\n- ${data.validationErrors.join("\n- ")}`;
          } else {
              return defaultErrorMessage();
          }
      }
        case ErrorCode.ActionModuleSchedulingImpossible:
            const data = res.data as Map<string, string>;
            if (data !== undefined) {
                const dataString = Object.keys(data)
                    .map(data => [data, data[data]])
                    .map(([k, v]) => `${k}: ${v}`)
                    .join('\n- ');
                return defaultErrorMessage()+`\nData:\n- ${dataString}`;
            }
            return defaultErrorMessage();
        default:
            return defaultErrorMessage();
    }
  };
  return (
    <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, width: "100%", maxWidth: "450px" }}>
      <Stack spacing={2}>
        
        {open && (
          <Slide direction="left" in={open} mountOnEnter unmountOnExit>
            <Alert severity="success" variant="filled" onClose={handleCloseSuccessMessage}>
              {message}
            </Alert>
          </Slide>
        )}

        {errorResponses?.map((errorObj, index) => (
          <Slide key={`${errorObj.traceId}-${index}`} direction="left" in={true}>
            <Alert
              severity="error"
              variant="filled"
              onClose={() => dispatch(removeErrorByIndex(index))}
              sx={{ whiteSpace: "pre-wrap", boxShadow: 3 }}
            >
              <Typography variant="caption" component="div" sx={{ fontFamily: "monospace" }}>
                {formatError(errorObj)}
              </Typography>
            </Alert>
          </Slide>
        ))}
        
      </Stack>
    </Box>
  );
}
